import React from 'react';
import { MessageSquare, GitBranch, CalendarCheck, Sparkles, Trash2 } from 'lucide-react';
import { useT, fmtDateTime } from '../salesI18n';
import { StatusBadge } from './StatusBadge';

const ICONS = { note: MessageSquare, status: GitBranch, visit: CalendarCheck, system: Sparkles };

// Traduz entradas codificadas ("status:a>b|motivo", "visit_scheduled:type|date time")
export const describe = (entry, t) => {
  const { kind, text } = entry;
  if (kind === 'system' && text === 'lead_created') return { title: t.tl.lead_created };
  if (kind === 'system') {
    const [ev, rest] = text.split(':');
    const [a, b] = (rest || '').split('|');
    if (ev === 'simulation_started') return { title: t.tlExtra.simulation_started, detail: `${a} ${t.photos.toLowerCase()} · ${b} ${t.after.toLowerCase()}` };
    if (ev.startsWith('simulation_')) return { title: t.tlExtra[ev] || ev, detail: `${a}/${b}` };
    if (ev === 'measurement_completed') return { title: t.tlExtra.measurement_completed, detail: `${a} ${t.openings.toLowerCase()}` };
    if (ev === 'proposal_created') return { title: t.tlExtra.proposal_created, detail: `${a} · ${b}` };
    if (ev === 'proposal_sent') return { title: t.tlExtra.proposal_sent, detail: `${a} → ${b}` };
    if (ev === 'contract_created') return { title: t.tlExtra.contract_created, detail: `${a} (${b})` };
    if (ev === 'contract_signed') return { title: t.tlExtra.contract_signed, detail: `${a} · ${b}` };
    if (ev === 'job_requested') return { title: t.tlExtra.job_requested, detail: `${a} 🪟 · ${b} 🚪` };
    if (ev === 'job_scheduled') return { title: t.tlExtra.job_scheduled, detail: `${a} · ${String(b || '').replace(/(\d{4})-(\d{2})-(\d{2})/, '$3/$2/$1')}` };
    if (ev.startsWith('customer_email_')) { const err = (rest || '').split('|')[2]; return { title: t.tlExtra[ev] || ev, detail: a === 'sent' ? `✅ ${b}` : `⚠️ ${t.tlExtra.customer_email_failed}${err ? ` · ${t.tlExtra.customer_email_errors[err] || err}` : ''}` }; }
    if (ev === 'customer_review') return { title: t.tlExtra.customer_review, detail: `${'⭐'.repeat(Number(a) || 0)} ${a}/5${b ? ` · “${b}”` : ''}` };
    if (ev === 'project_stage') return { title: t.tlExtra.project_stage, detail: t.projectStages[a] || a };
    if (ev === 'followup_called') return { title: t.tlExtra.followup_called, detail: `${a} · ${t.followUpNext} ${b}` };
    if (ev === 'proposal_viewed') return { title: t.tlExtra.proposal_viewed, detail: a };
    if (ev === 'proposal_interest') return { title: t.tlExtra.proposal_interest, detail: b ? `${a} · ${b}` : a };
    if (ev === 'proposal_accepted') return { title: t.tlExtra.proposal_accepted, detail: `${a} · ${b}` };
    if (ev === 'studio_photos') return { title: t.tlStudio.studio_photos };
    if (ev === 'estimate_requested') return { title: t.tlStudio.estimate_requested, detail: `${t.studioTimes[a] || a}${b ? ` · ${b}` : ''}` };
    if (ev === 'studio_plan') return { title: t.tlStudio.studio_plan, detail: `${a} · ${b}` };
    return { title: ev, detail: rest };
  }
  if (kind === 'status') {
    const [body, reason] = text.replace('status:', '').split('|');
    const [from, to] = body.split('>');
    return { title: t.tl.status, from, to, reason };
  }
  if (kind === 'visit') {
    const [head, when] = text.split('|');
    const [ev, type] = head.split(':');
    return { title: t.tl[ev] || ev, detail: `${t.visitType[type] || type} · ${when}` };
  }
  return { title: null, detail: text };
};

export const Timeline = ({ entries = [], onDeleteNote }) => {
  const { t, lang } = useT();
  if (!entries.length) return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="lead-timeline-empty">{t.noTimeline}</p>;
  return (
    <ol className="relative border-l border-border ml-3 space-y-5" data-testid="lead-timeline">
      {entries.map((e) => {
        const Icon = ICONS[e.kind] || MessageSquare;
        const d = describe(e, t);
        return (
          <li key={e.id} className="relative ml-5 sales-rise" data-testid={`timeline-entry-${e.kind}`}>
            <span className={`absolute -left-8 top-0 flex h-6 w-6 items-center justify-center rounded-full border bg-card ${e.kind === 'note' ? 'text-primary border-primary/40' : 'text-muted-foreground border-border'}`}>
              <Icon className="h-3 w-3" />
            </span>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {d.title && <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.title}</p>}
                {d.from && (
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <StatusBadge status={d.from} testId={`timeline-from-${d.from}`} /> <span className="text-muted-foreground">→</span> <StatusBadge status={d.to} testId={`timeline-to-${d.to}`} />
                    {d.reason && <span className="text-xs text-muted-foreground italic">· {d.reason}</span>}
                  </div>
                )}
                {d.detail && <p className={`text-sm mt-0.5 whitespace-pre-wrap ${e.kind === 'note' ? 'text-foreground' : 'text-muted-foreground'}`}>{d.detail}</p>}
                <p className="text-[11px] text-muted-foreground mt-1">{e.author_name || e.author} · {fmtDateTime(e.created_at, lang)}</p>
              </div>
              {e.kind === 'note' && onDeleteNote && (
                <button type="button" onClick={() => onDeleteNote(e)} className="text-muted-foreground hover:text-destructive p-1 rounded-md transition-colors" data-testid={`timeline-delete-note-${e.id}`} aria-label="delete note">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
